import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import SVG from 'react-inlinesvg';
import classNames from 'classnames/bind';
import Tooltip from '@material-ui/core/Tooltip';
import CircularLoader from '../../../shared/components/CircularLoader/CircularLoader';
import { useUpdatesStatus } from '../../hooks/useUpdatesStatus';
import styles from './updatesStatus.module.scss';

const conditionalStyles = classNames.bind(styles);

export default memo(function UpdatesStatus(props) {
    const { t } = useTranslation(['common', 'validations', 'dashboard']);

    useUpdatesStatus(props.getUpdatesRequest, props.getDevicesUpdatesRequest);

    const updatesErrorDescription = props.updates.error && !props.updates.qtty ? 'validations:ERROR' : 'dashboard:update_error';
    const devicesErrorDescription = props.devicesUpdates.error && !props.devicesUpdates.qtty
        ? 'validations:ERROR'
        : 'dashboard:update_error';

    const goToLegacyDevicesUpdatesSite = () => {
        const legacyDevicesUpdatesSiteAddress = process.env.NODE_ENV === 'development'
            ? 'http://192.168.144.106/admin/devices.php?tab=5'
            : `${window.location.protocol}//${window.location.hostname}/admin/devices.php?tab=5`;

        window.open(legacyDevicesUpdatesSiteAddress, '_blank');
    };

    const goToLegacyUpdatesSite = () => {
        const legacyUpdatesSiteAddress = process.env.NODE_ENV === 'development'
            ? 'http://192.168.144.106/admin/support.php'
            : `${window.location.protocol}//${window.location.hostname}/admin/support.php`;

        window.open(legacyUpdatesSiteAddress, '_blank');
    };

    const showWarningState =
        props.updates.error ||
        props.devicesUpdates.error ||
        props.updates.qtty > 0 ||
        props.devicesUpdates.qtty > 0;

    const showSuccessState =
        !props.updates.error &&
        !props.devicesUpdates.error &&
        props.updates.qtty === 0 &&
        props.devicesUpdates.qtty === 0;

    return (
        <div className={styles.container}>
            <div className={styles.informationContainer}>
                {
                    props.updates.fetching &&
                    <div className={styles.title}>
                        <SVG cacheGetRequests className={styles.icon} src="./icons/device.svg" />
                        <span>{t('dashboard:denwa_uc_updates')}</span>
                    </div>
                }
                {
                    !props.updates.fetching &&
                    <Tooltip
                        className={conditionalStyles({
                            title: true,
                            autoWidth: !props.updates.fetching && props.updates.qtty === 0
                        })}
                        disableFocusListener
                        disableTouchListener
                        title={t('dashboard:denwa_uc_updates')}
                    >
                        <div>
                            <SVG cacheGetRequests className={styles.icon} src="./icons/device.svg" />
                            <span>{t('dashboard:denwa_uc_updates')}</span>
                        </div>
                    </Tooltip>
                }
                <div className={styles.valueContainer}>
                    {props.updates.fetching && <CircularLoader size={24} warning={props.updates.error} />}
                    {
                        !props.updates.fetching &&
                        props.updates.error &&
                        <Tooltip disableFocusListener disableTouchListener title={t(updatesErrorDescription)}>
                            <div>
                                <SVG cacheGetRequests className={styles.error} src="./icons/error.svg" />
                            </div>
                        </Tooltip>
                    }
                    {
                        !props.updates.fetching &&
                        props.updates.qtty === 0 &&
                        <span className={styles.value}>{t('dashboard:updated')}</span>
                    }
                    {
                        !props.updates.fetching &&
                        props.updates.qtty > 0 &&
                        <div className={styles.linkArea} onClick={goToLegacyUpdatesSite}>
                            <span className={styles.forwardValue}>{t('dashboard:available_updates')}</span>
                            <SVG cacheGetRequests className={styles.forward} src="./icons/back.svg" />
                        </div>
                    }
                </div>
            </div>
            <div className={styles.informationContainer}>
                {
                    props.updates.fetching &&
                    <div className={styles.clockTitle}>
                        <SVG cacheGetRequests className={styles.icon} src="./icons/device_group.svg" />
                        <span>{t('dashboard:device_firmware_updates')}</span>
                    </div>
                }
                {
                    !props.updates.fetching &&
                    <Tooltip
                        className={conditionalStyles({
                            clockTitle: true,
                            autoWidth: !props.devicesUpdates.fetching && props.devicesUpdates.qtty === 0
                        })}
                        disableFocusListener
                        disableTouchListener
                        title={t('dashboard:device_firmware_updates')}
                    >
                        <div>
                            <SVG cacheGetRequests className={styles.icon} src="./icons/device_group.svg" />
                            <span>{t('dashboard:device_firmware_updates')}</span>
                        </div>
                    </Tooltip>
                }
                <div className={styles.valueContainer}>
                    {props.devicesUpdates.fetching && <CircularLoader size={24} warning={props.devicesUpdates.error} />}
                    {
                        !props.devicesUpdates.fetching &&
                        props.devicesUpdates.error &&
                        <Tooltip disableFocusListener disableTouchListener title={t(devicesErrorDescription)}>
                            <div>
                                <SVG cacheGetRequests className={styles.error} src="./icons/error.svg" />
                            </div>
                        </Tooltip>
                    }
                    {
                        !props.devicesUpdates.fetching &&
                        props.devicesUpdates.qtty === 0 &&
                        <span className={styles.value}>{t('dashboard:updated')}</span>
                    }
                    {
                        !props.devicesUpdates.fetching &&
                        props.devicesUpdates.qtty > 0 &&
                        <div className={styles.linkArea} onClick={goToLegacyDevicesUpdatesSite}>
                            <span className={styles.forwardValue}>{t('dashboard:available_updates')}</span>
                            <SVG cacheGetRequests className={styles.forward} src="./icons/back.svg" />
                        </div>
                    }
                </div>
            </div>
            {showWarningState && <div className={styles.warning} />}
            {showSuccessState && <div className={styles.success} />}
        </div>
    );
}, (prevProps, nextProps) =>
        prevProps.updates === nextProps.updates &&
        prevProps.devicesUpdates === nextProps.devicesUpdates &&
        prevProps.getUpdatesRequest === nextProps.getUpdatesRequest &&
        prevProps.getDevicesUpdatesRequest === nextProps.getDevicesUpdatesRequest
);
